import {
  PracticeContentDataTypes,
  PracticeDetailFields,
  RecommendationPointsModel,
  RecommendationsAndAcceptanceScoreForPoolsInputAPI,
} from '@cibo/core'
import { usePracticesContent } from '@cibo/landmanager'
import { logRequestError } from '@cibo/ui'
import { useEffect, useMemo, useState } from 'react'
import { useProgramEngagementDetails } from '../../queries'
import { CombinedPractice, UseEqipProgramEngagementPracticesProps } from './practicesListTypes'

export const useEqipProgramEngagementPractices = ({
  engagementId,
  selectedPoolName,
}: UseEqipProgramEngagementPracticesProps) => {
  const details = useProgramEngagementDetails({
    engagementId,
    detailRequirements: [{ traitId: 'eqipRecommendedPracticesIntent', dataType: 'workflow' }],
  })
  const practiceContent = usePracticesContent()
  const [selectedPractices, setSelectedPractices] = useState<string[]>([])
  const [dataUpdatedAt, setDataUpdatedAt] = useState<Date>()
  const [loadFailed, setLoadFailed] = useState(false)
  const [doFullLoad, setDoFullLoad] = useState<boolean>(true)
  const [isPending, setIsPending] = useState<boolean>()
  const [recommendationsPointsSummary, setRecommendationsPointsSummary] =
    useState<RecommendationPointsModel>()

  useEffect(() => {
    setIsPending(true)
    RecommendationsAndAcceptanceScoreForPoolsInputAPI.summary({
      engagementId,
      selectedPractices,
      doFullLoad,
    })
      .then(data => {
        setDoFullLoad(false)
        setRecommendationsPointsSummary(new RecommendationPointsModel(data))
        setDataUpdatedAt(new Date())
      })
      .catch(error => {
        logRequestError(error)
        setLoadFailed(true)
      })
      .finally(() => setIsPending(false))
  }, [engagementId, selectedPractices.join(',')])

  useEffect(() => {
    setSelectedPractices((details?.data?.[0]?.result as string[]) || [])
  }, [details.dataUpdatedAt])

  const practicesContentItems = practiceContent.data?.items as unknown as PracticeContentDataTypes[]
  const practices = useMemo<readonly CombinedPractice[]>(() => {
    const relevantPractices: CombinedPractice[] = []

    practicesContentItems?.forEach((allPractices: PracticeContentDataTypes) => {
      const matchingPractice = recommendationsPointsSummary?.practicePointsByPoolName(
        (allPractices.fields as unknown as PracticeDetailFields).practiceId
      )

      if (matchingPractice) {
        relevantPractices.push({ ...allPractices, points: matchingPractice })
      }
    })

    if (selectedPoolName) {
      relevantPractices.sort((practiceA: CombinedPractice, practiceB: CombinedPractice) => {
        if (selectedPoolName) {
          const pointEffectDiff =
            (practiceB.points[selectedPoolName]?.effect || -0) -
            (practiceA.points[selectedPoolName]?.effect || -0)

          if (pointEffectDiff !== 0) {
            return pointEffectDiff
          }
          if (
            practiceA.points[selectedPoolName] !== undefined &&
            practiceB.points[selectedPoolName] === undefined
          )
            return -1
          if (
            practiceA.points[selectedPoolName] === undefined &&
            practiceB.points[selectedPoolName] !== undefined
          )
            return 1

          const addonA = practiceA.points[selectedPoolName]?.isAddon
          const addonB = practiceB.points[selectedPoolName]?.isAddon
          if (addonB && !addonA) return 1
          if (addonA && !addonB) return -1
          return 0
        } else {
          return 0
        }
      })
    }

    return relevantPractices as readonly CombinedPractice[]
  }, [practiceContent.dataUpdatedAt, dataUpdatedAt, selectedPoolName])

  return {
    practices,
    practiceContentQuery: practiceContent,
    detailsQuery: details,
    isPending,
    isLoading:
      loadFailed || practiceContent.isLoading || !recommendationsPointsSummary || isPending,
  }
}
